import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDevicePlaneDevices } from '../api/devicePlane.js';
import { downloadCsv } from '../lib/csv.js';

const selectCls =
  'bg-surface border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent';

const KIND_LABEL: Record<string, string> = {
  stair: 'Stair light',
  ceiling: 'Ceiling light',
  lift: 'Lift',
  unknown: '—',
};

export function DevicePlaneFleet() {
  const [presence, setPresence] = useState('');
  const [family, setFamily] = useState('');
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['dp-fleet', presence, family, kind, search],
    queryFn: () =>
      getDevicePlaneDevices({
        ...(presence ? { presence } : {}),
        ...(family ? { family } : {}),
        ...(kind ? { kind } : {}),
        ...(search ? { search } : {}),
        limit: 200,
      }),
    refetchInterval: 15000,
  });

  const items = data?.items ?? [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink">Device Plane — Fleet</h1>
          <p className="text-sm text-muted mt-0.5">
            Every device the gateway knows (registry mirror) with live presence and category. {data ? `${data.total} device(s).` : ''}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search serial / device id"
            className={`${selectCls} w-56 placeholder:text-muted`}
          />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={selectCls} aria-label="Category filter">
            <option value="">All categories</option>
            <option value="stair">Stair lights</option>
            <option value="ceiling">Ceiling lights</option>
            <option value="lift">Lifts</option>
            <option value="unknown">Uncategorised</option>
          </select>
          <select value={presence} onChange={(e) => setPresence(e.target.value)} className={selectCls} aria-label="Presence filter">
            <option value="">All presence</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="unknown">Unknown</option>
          </select>
          <select value={family} onChange={(e) => setFamily(e.target.value)} className={selectCls} aria-label="Family filter">
            <option value="">All families</option>
            <option value="m8">M8 (lights)</option>
            <option value="cop-lop">COP-LOP (lift)</option>
          </select>
          <button
            onClick={() =>
              downloadCsv(
                'device-plane-fleet',
                items.map((d) => ({
                  serialNumber: d.serialNumber,
                  deviceId: d.deviceId,
                  family: d.family,
                  category: d.kind,
                  lifecycle: d.lifecycleState,
                  presence: d.presence,
                  lastSeenAt: d.lastSeenAt ?? '',
                })),
              )
            }
            className="px-3 py-1.5 rounded-lg bg-accent text-white text-sm hover:opacity-90"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-surface border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted border-b border-grid">
              <th className="px-4 py-2.5">Serial</th>
              <th className="px-4 py-2.5">Device ID</th>
              <th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5">Family</th>
              <th className="px-4 py-2.5">Lifecycle</th>
              <th className="px-4 py-2.5">Presence</th>
              <th className="px-4 py-2.5">Last seen</th>
              <th className="px-4 py-2.5 text-right">Uptime 7d</th>
              <th className="px-4 py-2.5 text-right">Sync age</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.deviceId} className="border-b border-grid hover:bg-surface2">
                <td className="px-4 py-2.5 font-mono text-xs">
                  <Link to={`/device-plane/fleet/${d.deviceId}`} className="text-accent hover:underline">
                    {d.serialNumber}
                  </Link>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted">{d.deviceId.slice(0, 8)}…</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-block text-xs font-medium rounded-full px-2 py-0.5 ${
                    d.kind === 'unknown' ? 'bg-surface2 text-muted' : 'bg-accent/15 text-[#a9c9f2]'
                  }`}>
                    {KIND_LABEL[d.kind] ?? d.kind}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-ink2">{d.family}</td>
                <td className="px-4 py-2.5 text-ink2">
                  {d.lifecycleState}
                  {d.firstOnlinePending && (
                    <span className="ml-1.5 inline-block text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-warn/15 text-warnink"
                      title="Registered but never seen online">
                      never online
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                      d.presence === 'online' ? 'text-goodink' : d.presence === 'offline' ? 'text-badink' : 'text-muted'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        d.presence === 'online' ? 'bg-good' : d.presence === 'offline' ? 'bg-bad' : 'bg-muted'
                      }`}
                    />
                    {d.presence}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted">
                  {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : '—'}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums text-xs ${
                  d.uptimePct7d === null ? 'text-muted'
                    : d.uptimePct7d >= 98 ? 'text-goodink'
                    : d.uptimePct7d >= 90 ? 'text-warnink' : 'text-badink'
                }`}>
                  {d.uptimePct7d === null ? '—' : `${d.uptimePct7d}%`}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-xs text-muted">
                  {d.syncAgeSec === null ? '—'
                    : d.syncAgeSec < 90 ? `${d.syncAgeSec} s`
                    : d.syncAgeSec < 5400 ? `${Math.round(d.syncAgeSec / 60)} m`
                    : d.syncAgeSec < 129600 ? `${Math.round(d.syncAgeSec / 3600)} h`
                    : `${Math.round(d.syncAgeSec / 86400)} d`}
                </td>
              </tr>
            ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted">
                  No devices in the gateway registry yet — they appear on registration or bootstrap.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
